import { Box, LinearProgress, Link, Typography } from '@ace-clublabs/shared-component-library';
import {
  getClubPhoneNumber,
  getFooterContent,
  getFooterDisclaimer,
  getIdentityEnv,
  getIdentityRedirectUri,
  identityLoginUrl,
  getClubCode,
} from '../utils/methods';
import Spinner from './Spinner';
import { Header } from './Header';
import { useEffect, useState } from 'react';
import { AutoPayProduct } from './AutoPayProduct';
import { IGetAutoPayDetailsResponse, ErrorType, IMembershipProduct, IInsuranceProduct } from '../shared/types';
import Cookies from 'universal-cookie';
import { TOKEN_KEY_PAYMENTS, TOKEN_KEY_MAX_AGE } from '../shared/constants';
//@ts-ignore
import AceIdentity, { AceIdentityExternalIdp } from '@ace-clublabs/ace-identity';
import { useGlobalState } from '../shared/state';
import { sources } from '../api/sources';
import { useNavigate } from 'react-router-dom';
import { Pages } from '../shared/pages';
import { UtagHelper, UtagParam } from '../utils/utagHelper';
import LogCannon from '@ace-clublabs/log-cannon';
import { Breadcrumb } from '@ace-clublabs/payments-component-library';
import { getBillingSummaryUrl, getMyAccountURL, getPolicyViewUrl } from '@ace-clublabs/payments-helper';
import { PolicyType, ICreateManifestRequest } from '../shared/types';
import { ProductType } from '@ace-clublabs/payment-types';
import { BrokerRegion } from '@lib/types';
import infoTag from '../assets/info_tag.svg';

const cookies = new Cookies();
const REFERRER_CONTEXT_KEY = 'manageAutopayReferrerContext';

const getReferrerCategory = (value?: string) => {
  if (!value) return undefined;
  const normalizedReferrer = value.toLowerCase();
  if (normalizedReferrer.includes('billingsummary')) return 'billingsummary';
  if (normalizedReferrer.includes('policyview')) return 'policyview';
  if (normalizedReferrer.includes('manage-saved-payment-methods')) return 'manage-saved-payment-methods';
  if (normalizedReferrer.includes('membership/selfservice')) return 'membership/selfservice';
  return undefined;
};

const isKnownReferrer = (value?: string) => {
  return !!getReferrerCategory(value);
};

const getOriginFromReferrer = (documentReferrer?: string) => {
  if (!documentReferrer?.startsWith('http')) return undefined;
  try {
    return new URL(documentReferrer).origin;
  } catch {
    return undefined;
  }
};

const getSyntheticReferrerFromKeyword = (referrerParam?: string, documentReferrer?: string, guid?: string) => {
  if (!referrerParam) return undefined;
  const normalizedReferrer = referrerParam.toLowerCase();
  const origin = getOriginFromReferrer(documentReferrer);
  if (!origin) return undefined;

  if (normalizedReferrer.includes('policyview')) {
    return guid ? `${origin}/insurance/policyview/${guid}` : `${origin}/insurance/policyview/`;
  }
  if (normalizedReferrer.includes('billingsummary')) return getBillingSummaryUrl();
  if (normalizedReferrer.includes('manage-saved-payment-methods')) return `${origin}/payments/manage-saved-payment-methods`;
  if (normalizedReferrer.includes('membership/selfservice')) return `${origin}/membership/selfservice`;
  return undefined;
};

const getPreferredReferrer = (
  referrerParam?: string,
  documentReferrer?: string,
  guid?: string,
  savedReferrer?: string
) => {
  if (referrerParam) {
    if (referrerParam.startsWith('http')) return referrerParam;

    const referrerParamCategory = getReferrerCategory(referrerParam);
    const documentReferrerCategory = getReferrerCategory(documentReferrer);
    const isSameCategoryAsDocumentReferrer =
      !!referrerParamCategory && referrerParamCategory === documentReferrerCategory && documentReferrer?.startsWith('http');
    if (isSameCategoryAsDocumentReferrer) return documentReferrer;

    const savedReferrerCategory = getReferrerCategory(savedReferrer);
    const isSameCategoryAsSavedReferrer =
      !!referrerParamCategory && referrerParamCategory === savedReferrerCategory && savedReferrer?.startsWith('http');
    if (isSameCategoryAsSavedReferrer) return savedReferrer;

    const syntheticReferrer = getSyntheticReferrerFromKeyword(referrerParam, documentReferrer, guid);
    if (syntheticReferrer) return syntheticReferrer;
    return referrerParam;
  }

  return documentReferrer;
};

interface PolicyViewContext {
  mode?: string;
  policyNumber?: string;
}

const getAccessToken = async () => {
  //uncomment this to test locally
  // return 'eyJraWQiOiJIcnlEM1plSXE5TjRJZGhUNWRuQjNrSHdsRE4yR2pjXzNsZFRxMF90cDZrIiwiYWxnIjoiUlMyNTYifQ.eyJ2ZXIiOjEsImp0aSI6IkFULmRDc011cXZheHRLMlJzWnFWZlNWLUY0LXhjT2VqTjA4NHhubEsxbFZCYVEub2FyM3h4dmJjbk1NSUFTTE0waDciLCJpc3MiOiJodHRwczovL25vbnByb2QtYXV0aC5pZGVudGl0eS5hY2UuYWFhLmNvbS9vYXV0aDIvYXVzbW9mcXVvNjJjSHRZTmswaDciLCJhdWQiOiIwb2Ftbjk1Y3I2Zko4MFl0VjBoNyIsImlhdCI6MTc3NTA2ODU2OCwiZXhwIjoxNzc1MDcyMTY4LCJjaWQiOiIwb2Ftbjk1Y3I2Zko4MFl0VjBoNyIsInVpZCI6IjAwdTJna2NienZvdTVKMHpyMGg4Iiwic2NwIjpbIm9mZmxpbmVfYWNjZXNzIiwicHJvZmlsZSIsInNlbGZSZWNvdmVyeSIsIm9wZW5pZCIsImNsdWJDb2RlIiwic2NvcGUiLCJjdXN0b21lcklkIl0sImF1dGhfdGltZSI6MTc3NTA2ODU2OCwiemlwQ29kZSI6Ijc1MDYwIiwic3ViIjoiMjUyXzEwNTYwNzA0QGFhYS5jb20iLCJzZWxmUmVjb3ZlcnkiOmZhbHNlLCJjbHViQ29kZSI6IjI1MiIsIm1lbWJlck51bWJlciI6IjYyMDI1MjEwNTYwNzA0MDAiLCJzY29wZSI6W10sImN1c3RvbWVySWQiOiI5NTg2NzgwMjAwNzIzNDciLCJwcmVmZXJyZWRfdXNlcm5hbWUiOiIyNTJfMTA1NjA3MDRAYWFhLmNvbSIsIm1lbWJlcnNoaXBTdGF0dXMiOiJ2YWxpZCIsImVtYWlsIjoiMjUyXzEwNTYwNzA0QGFhYS5jb20ifQ.XTMmoBHj9S6Ni2xtrIGz8QYvico2bJVYg1nGmZOgBSOg10ryB2tAXyh2hXfLjh-m8Met72kToLlpIYvGwo5cHxmQb68Y0-2vsAFfHISTUju9XVZcy5LQlVfKn6X5gcSHEGYfBvyuePVxnL86csT1vAf_4Yaqh-E9U9LsWkDag5hcwheFWQd7GsrbyZ4zmmrgt26p0iZ1bYkSj-FnXhUIr7TR_kmfOttM907qtEQTT-p-Nfi18sbIkp5WPshI5wBa3awLzE5JdFebOjymqhv_EandvMA1UbpakaJj1jU-mc0rZkWmJPSs5QFZ2CvmPDQgGxAT-E9fqH1auzax80iOsQ';
  // Set test token from environment variable for local testing
  if (window.location.hostname === 'localhost' && import.meta.env.VITE_LOCAL_TEST_TOKEN) {
    const accessToken = import.meta.env.VITE_LOCAL_TEST_TOKEN;
    cookies.set(TOKEN_KEY_PAYMENTS, accessToken, { path: '/' });
    return accessToken;
  }

  const queryParams = new URLSearchParams(window.location.search);
  const isAne = queryParams.get('idp') === 'ane';

  const env = getIdentityEnv();
  const aceIdentity = new AceIdentity({
    env,
    redirectUri: getIdentityRedirectUri(),
    loginUrl: identityLoginUrl(),
    disableBypassPkce: false,
    bypassAuth2: env === 'non-prod' ? true : false,
    scopes: isAne ? ['email', 'profile', 'openid', 'sso.idp.ane'] : undefined,
    idp: isAne ? AceIdentityExternalIdp.Ane : undefined,
  });
  await aceIdentity.ready();
  const { accessToken } = await aceIdentity.tokens();
  cookies.set(TOKEN_KEY_PAYMENTS, accessToken, { path: '/', maxAge: TOKEN_KEY_MAX_AGE });
  return accessToken as string;
};

export const ManageAutoPay: React.FC = () => {
  const [isLoading, setLoading] = useState(true);
  const [autoPayDetails, setAutoPayDetails] = useState({} as IGetAutoPayDetailsResponse);
  const [billPlan, setBillPlan] = useGlobalState('billPlan');
  const [clubCode, setClubCode] = useGlobalState('clubCode');
  const sessionId = LogCannon.getSessionId();
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const [, setEmailOnFile] = useGlobalState('emailOnFile');
  const [, setPaymentUpdated] = useGlobalState('paymentUpdated');
  const [referrer, setReferrer] = useGlobalState('referrer');
  const [isMobileApp, setIsMobileApp] = useState(false);
  const [policyViewContext, setPolicyViewContext] = useState<PolicyViewContext | undefined>(undefined);
  const [, setMemcid] = useGlobalState('memcid');
  const [brokerRegionFromState, setBrokerRegion] = useGlobalState('brokerRegion');
  const [memcid] = useGlobalState('memcid');
  const [, setConfirmationVisited] = useGlobalState('confirmationVisited');
  const [, setConfirmationDetails] = useGlobalState('confirmationDetails');
  const [, setTransactionCheckMade] = useGlobalState('transactionCheckMade');
  const [, setIsInitialRender] = useGlobalState('isInitialRender');

  const navigate = useNavigate();

  let localBillPlan = '';

  const tag = (clubCode: string, returnedTransactionSuccessful?: boolean) => {
    let tags = {
      ...UtagHelper.CommonTaggingValues,
      clubcode: clubCode,
      page: UtagHelper.Pages.manageAutoPay,
      sessionid: sessionId,
      ...(localBillPlan && { mem_billplan: localBillPlan }),
      ...(returnedTransactionSuccessful && { manageautopay: 'true' }),
    } as UtagParam;
    UtagHelper.tag(tags);
  };

  const getAutoPayDetails = async (transactionId?: string, tagClubCode?: string, brokerRegion?: BrokerRegion) => {
    try {
      tagClubCode = tagClubCode ?? clubCode;
      const autoPayDetails = await sources.getAutoPayDetails({ transactionId, brokerRegion });
      setAutoPayDetails(autoPayDetails);
      const membershipDetails = autoPayDetails?.membership;
      localBillPlan = membershipDetails?.billPlan?.toLowerCase() ?? '';
      setBillPlan(localBillPlan);

      if (transactionId) {
        tag(tagClubCode, true);
        setPaymentUpdated(true);
      } else {
        tag(tagClubCode);
      }

      if (autoPayDetails.emailOnFile) setEmailOnFile(true);
      setLoading(false);
    } catch (e: any) {
      const authErrors = [ErrorType.SESSION_TIMEOUT, ErrorType.AUTH_ERROR];
      if (authErrors.includes(e.type)) {
        const accessToken = await getAccessToken();
        setAccessToken(accessToken);
        getAutoPayDetails(transactionId, tagClubCode);
      } else if (e.type === ErrorType.PROCESSING_ERROR) {
        navigate(Pages.Error, { state: { type: ErrorType.PROCESSING_ERROR } });
      } else {
        navigate(Pages.Error, { state: { type: ErrorType.SYSTEM_ERROR } });
      }
    }
  };

  const sendManifestRequest = async (selectedProduct: IMembershipProduct | IInsuranceProduct) => {
    setIsInitialRender(false);
    const productType =
      selectedProduct?.policyType === PolicyType.Membership ? ProductType.Membership : ProductType.Insurance;
    if (productType === ProductType.Membership && selectedProduct.minDue! > 0 && selectedProduct.hasPIT) {
      return navigate(Pages.Error, { state: { type: ErrorType.MEMBERSHIP_PIT_ERROR } });
    }

    let manifestRequest: ICreateManifestRequest = {
      productId: selectedProduct?.productId,
      productType,
      newEnroll: false,
      brokerRegion: brokerRegionFromState,
      tags: {
        ...UtagHelper.CommonTaggingValues,
        clubcode: clubCode,
        page: UtagHelper.Pages.payment,
        sessionid: sessionId,
        mem_billplan: billPlan,
        ...(selectedProduct && { flow: productType === ProductType.Membership ? 'membership' : 'insurance' }),
      },
    };
    if (memcid) manifestRequest = { ...manifestRequest, memcid: memcid };

    try {
      setLoading(true);
      setConfirmationVisited(false);
      setConfirmationDetails({} as any);
      setTransactionCheckMade(false);

      const { manifestId } = await sources.createManifest(manifestRequest);
      const url = `https://${window.location.hostname}/payments/${manifestId}`;
      window.location.assign(url);
    } catch (e: any) {
      navigate(Pages.Error, { state: { type: ErrorType.SYSTEM_ERROR } });
    }
  };

  useEffect(() => {
    let mobileAppFromSession = sessionStorage.getItem('mobileapp');
    if (!mobileAppFromSession) {
      const url = window.location.href;
      const params = new URLSearchParams(new URL(url).search);
      const mobileApp = params.get('mobileapp');
      if (mobileApp === 'true') {
        sessionStorage.setItem('mobileapp', 'true');
        setIsMobileApp(true);
      }
    } else setIsMobileApp(mobileAppFromSession == 'true');
  }, []);

  useEffect(() => {
    let url = window.location.href;
    let updatedUrl;

    const params = new URLSearchParams(new URL(url).search);
    const documentReferrer = document.referrer;

    const memcidInUrl = params.get('memcid') || undefined;
    if (memcidInUrl) setMemcid(memcidInUrl);

    const referrerParam = params.get('referrer') || undefined;
    const guidInUrl = params.get('guid') || undefined;
    const modeInUrl = params.get('type')?.toLowerCase() || undefined;
    const policyNumberInUrl = params.get('policyNumber') || undefined;

    const referrerFromPolicyViewKeyword =
      !!referrerParam && !referrerParam.startsWith('http') && referrerParam.toLowerCase().includes('policyview');

    if (referrerFromPolicyViewKeyword && (modeInUrl || policyNumberInUrl)) {
      const contextToSave = {
        ...(modeInUrl && { mode: modeInUrl }),
        ...(policyNumberInUrl && { policyNumber: policyNumberInUrl }),
      };
      sessionStorage.setItem(REFERRER_CONTEXT_KEY, JSON.stringify(contextToSave));
    }

    const savedPolicyViewContext = sessionStorage.getItem(REFERRER_CONTEXT_KEY);
    if (savedPolicyViewContext) {
      try {
        setPolicyViewContext(JSON.parse(savedPolicyViewContext));
      } catch {
        sessionStorage.removeItem(REFERRER_CONTEXT_KEY);
      }
    }

    const savedReferrer = sessionStorage.getItem('manageAutopayReferrer') || undefined;
    const preferredReferrer = getPreferredReferrer(referrerParam, documentReferrer, guidInUrl, savedReferrer);

    if (referrerParam && preferredReferrer) {
      sessionStorage.setItem('manageAutopayReferrer', preferredReferrer);
    } else if (!sessionStorage.getItem('manageAutopayReferrer')) {
      const referrerToSave = preferredReferrer;
      if (referrerToSave) sessionStorage.setItem('manageAutopayReferrer', referrerToSave);
    }
    const resolvedReferrer = referrerParam ? preferredReferrer : savedReferrer || preferredReferrer;

    const shouldUpdateReferrer = !!resolvedReferrer && resolvedReferrer !== referrer;

    if (shouldUpdateReferrer) {
      setReferrer(resolvedReferrer);
      updatedUrl = removeQueryParam(url, 'referrer');

      const productSubTypeInUrl = params.get('type') || undefined;
      if (productSubTypeInUrl) {
        updatedUrl = removeQueryParam(updatedUrl, 'type');
      }

      if (guidInUrl) {
        updatedUrl = removeQueryParam(updatedUrl, 'guid');
      }

      const policyNumberInUrl = params.get('policyNumber') || undefined;
      if (policyNumberInUrl) {
        updatedUrl = removeQueryParam(updatedUrl, 'policyNumber');
      }
    }

    window.history.pushState(null, '', updatedUrl);

    if (referrer && referrer === resolvedReferrer) {
      return;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        window.location.reload();
      }
    });

    return () => {
      window.removeEventListener('pageshow', () => { });
    };
  }, []);

  useEffect(() => {
    if (accessToken) {
      setAccessToken(accessToken);
      const tagClubCode = getClubCode(accessToken);
      setClubCode(tagClubCode);

      const url = window.location.href;
      const params = new URLSearchParams(new URL(url).search);
      const transactionIdInUrl = params.get('transactionId') || undefined;
      if (transactionIdInUrl) {
        const updatedUrl = removeQueryParam(url, 'transactionId');
        window.history.pushState(null, '', updatedUrl);
      }

      let brokerRegionFromUrl = (params.get('backend')?.toUpperCase() as BrokerRegion) || undefined;
      if (brokerRegionFromUrl === BrokerRegion.A1 || brokerRegionFromUrl === BrokerRegion.B1) {
        setBrokerRegion(brokerRegionFromUrl);
      }
      getAutoPayDetails(transactionIdInUrl, tagClubCode, brokerRegionFromUrl || brokerRegionFromState);
    }
  }, [accessToken, sessionId]);

  useEffect(() => {
    setLoading(true);
    getAccessToken().then((accessToken) => {
      setAccessToken(accessToken);
      setClubCode(getClubCode(accessToken));
    });
  }, []);

  if (isLoading) {
    return (
      <>
        <Header />
        <Spinner />
      </>
    );
  }

  return (
    <ManageAutoPayView
      referrer={referrer}
      policyViewContext={policyViewContext}
      mobileApp={isMobileApp}
      autoPayDetails={autoPayDetails}
      clubCode={clubCode || '004'}
      handleSubmit={sendManifestRequest}
      showLoading={setLoading}
    />
  );
};

export interface ManageAutoPayProps {
  referrer: string;
  policyViewContext?: PolicyViewContext;
  mobileApp: boolean;
  clubCode: string;
  autoPayDetails: IGetAutoPayDetailsResponse;
  showLoading: (input: boolean) => void;
  handleSubmit: (input: IMembershipProduct | IInsuranceProduct) => void;
}

export const ManageAutoPayView: React.FC<ManageAutoPayProps> = (props) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedProduct] = useGlobalState('selectedProduct');
  const { clubCode, autoPayDetails, referrer, handleSubmit, policyViewContext } = props;
  const billPlan = autoPayDetails?.membership?.billPlan;
  const isMobileApp = props.mobileApp;
  const isCA = clubCode === '004';
  const isTXECMDO = '252,215'.includes(clubCode) && billPlan?.toLowerCase() === 'mp';
  const eCheckOnlyPolicyPrefixes = 'TXU,TXW,NMU,HIU';

  const hasECheckOnlyPolicy =
    autoPayDetails?.policies.find((product) => {
      return eCheckOnlyPolicyPrefixes.includes(product.productId.substring(0, 3));
    }) !== undefined;

  const eCheckOnlyPolicyTypes = '601,018'.includes(clubCode) ? 'Personal umbrella' : 'Watercraft and personal umbrella';
  const isMembershipAutoPayAllowed = autoPayDetails?.membership?.autoPay?.autoPayAllowed;
  const atleastOneInsuranceIsNotAllowed = autoPayDetails?.policies?.some(
    (policy) => policy.autoPay.autoPayAllowed === false
  );
  const isAne = clubCode === '240';
  const disableAutoPayEnrollAndUpdateCTA = isAne
    ? atleastOneInsuranceIsNotAllowed
    : !isMembershipAutoPayAllowed || atleastOneInsuranceIsNotAllowed;

  const acceptedMethodsText = () => {
    if (isCA) {
      return (
        <>
          <span style={{ display: 'flex', flexDirection: 'row' }}>
            <Typography variant="b1Regular" className="accepted-methods-list-one list-dot">
              •
            </Typography>
            <Typography variant="b1Regular" className="accepted-methods-list-one" sx={{ width: '85%' }}>
              Membership payments can be made with a credit card, debit card, or e-check.
            </Typography>
          </span>
          <span style={{ display: 'flex', flexDirection: 'row' }}>
            <Typography variant="b1Regular" className="accepted-methods-list-one list-dot">
              •
            </Typography>
            <Typography variant="b1Regular" className="accepted-methods-list-one" sx={{ width: '85%' }}>
              Insurance payments can be made with a debit card or e-check.
            </Typography>
          </span>
        </>
      );
    } else if (isTXECMDO) {
      return (
        <>
          <span style={{ display: 'flex', flexDirection: 'row' }}>
            <Typography variant="b1Regular" className="accepted-methods-list-one list-dot">
              •
            </Typography>
            <Typography variant="b1Regular" className="accepted-methods-list-one" sx={{ width: '85%' }}>
              Membership payments can be made with a credit card or debit card.
            </Typography>
          </span>
          <span style={{ display: 'flex', flexDirection: 'row' }}>
            <Typography variant="b1Regular" className="accepted-methods-list-one list-dot">
              •
            </Typography>
            <Typography variant="b1Regular" className="accepted-methods-list-one" sx={{ width: '85%' }}>
              Insurance payments can be made with a credit card, debit card, or e-check.
            </Typography>
          </span>
        </>
      );
    } else {
      return (
        <span style={{ display: 'flex', flexDirection: 'row' }}>
          <Typography variant="b1Regular" className="accepted-methods-list-one list-dot">
            •
          </Typography>
          <Typography variant="b1Regular" className="accepted-methods-list-one" sx={{ width: '85%' }}>
            {membershipDetails ? ("Membership and insurance") : ("Insurance")} payments{hasECheckOnlyPolicy ? '¹' : ''} can be made with a credit card, debit
            card, or e-check.
          </Typography>
        </span>
      );
    }
  };

  const renderAlert = () => {
    const message = <span>Only the primary policyholder/member can make changes to Auto Pay.</span>;
    const tagIcon = <img id="info-tag-img" alt="info-tag-icon" src={infoTag} />;
    return (
      <Box id="warning-message" className="autopay-warning">
        <Box className="icon-container">{tagIcon}</Box>
        <Typography variant="b2Regular">{message}</Typography>
      </Box>
    );
  };

  const phoneNumber = getClubPhoneNumber(clubCode);

  const policyDetails = autoPayDetails.policies;
  const membershipDetails = autoPayDetails.membership;
  const productElements =
    policyDetails.map((item) => (
      <AutoPayProduct
        product={item}
        disableAutoPayEnrollAndUpdateCTA={disableAutoPayEnrollAndUpdateCTA}
        handleChange={handleSubmit}
      ></AutoPayProduct>
    )) || [];

  let membershipElement;

  const updateProcessing = (value: boolean) => {
    setIsProcessing(value);
  };

  membershipDetails && Object.keys(membershipDetails).length > 0 ? (
    (membershipElement = (
      <AutoPayProduct
        product={membershipDetails as IMembershipProduct}
        disableAutoPayEnrollAndUpdateCTA={disableAutoPayEnrollAndUpdateCTA}
        updateProcessing={updateProcessing}
        showLoading={props.showLoading}
        handleChange={handleSubmit}
        referrer={referrer}
        phoneNumber={phoneNumber}
      ></AutoPayProduct>
    ))
  ) : (
    <></>
  );

  const renderTermsAndConditions = () => {
    return clubCode !== '240' && (
      <Box id="footer">
        {' '}
        <Typography variant="b2Regular" className="terms-one">
          {getFooterDisclaimer(clubCode)}{' '}
          <Link data-quid="privacy-policy-link" href="https://www.ace.aaa.com/legal/privacy.html">
            Privacy policy
          </Link>{' '}
          |{clubCode === '004' && ' '}
          {clubCode === '004' && (
            <Link data-quid="do-not-sell-link" href="https://apps2.calif.aaa.com/cpa/request">
              Do not sell my personal information
            </Link>
          )}{' '}
          {clubCode === '004' && '| '}
          <Link data-quid="accessibility-link" href="https://www.ace.aaa.com/digital-accessibility.html">
            Accessibility
          </Link>{' '}
          |{' '}
          <Link data-quid="term-of-use-link" href="https://www.ace.aaa.com/legal/terms-of-use.html">
            Terms of use
          </Link>{' '}
          |{' '}
          <Link data-quid="site-map-link" href="https://www.ace.aaa.com/sitemap.html">
            Site map
          </Link>{' '}
          |{' '}
          <Link data-quid="find-club-link" href="https://www.aaa.com/stop/">
            Find a different AAA club
          </Link>
        </Typography>
        <br></br>
        <Typography variant="b2Regular" className="terms-two">
          {getFooterContent(clubCode)}
        </Typography>
      </Box>
    );
  };

  const getBreadCrumbLinks = () => {
    const currentHostName = window.location.hostname;
    const homeBread = { label: 'Home', link: 'https://www.ace.aaa.com/' };
    const myAccountBread = { label: 'My Account', link: getMyAccountURL() };
    const mapBread = {
      label: 'Manage Auto Pay',
      link: `https://${currentHostName}/payments/manage-autopay`,
    };

    const baseBreadcrumbs = isAne ? [] : [homeBread, myAccountBread];

    if (referrer?.toLowerCase().includes('billingsummary')) {
      const billingSummaryBread = { label: 'Billing summary', link: referrer };
      return [...baseBreadcrumbs, billingSummaryBread, mapBread];
    } else if (referrer?.toLowerCase().includes('policyview')) {
      const shouldUseFlowDirectorPost = policyViewContext?.mode && policyViewContext?.policyNumber;
      const policyViewBread = {
        label: 'Policy view',
        link: shouldUseFlowDirectorPost ? getPolicyViewUrl() : referrer,
        ...(shouldUseFlowDirectorPost && {
          payload: new Map([
            ['policyNumber', policyViewContext.policyNumber!],
            ['clubCode', clubCode],
            ['mode', policyViewContext.mode!],
            ['redirectMode', policyViewContext.mode!],
          ]),
        }),
      };
      return [...baseBreadcrumbs, policyViewBread, mapBread];
    } else if (referrer?.toLowerCase().includes('manage-saved-payment-methods')) {
      const manageSavedPaymentMethodBread = {
        label: 'Manage saved payment methods',
        link: referrer,
      };
      return [...baseBreadcrumbs, manageSavedPaymentMethodBread, mapBread];
    } else if (referrer?.toLowerCase().includes('membership/selfservice')) {
      const membershpSelfServiceBread = {
        label: 'My Membership',
        link: referrer,
      };
      return [...baseBreadcrumbs, membershpSelfServiceBread, mapBread];
    } else {
      return [...baseBreadcrumbs, mapBread];
    }
  };

  const breadcrumbItems = getBreadCrumbLinks();

  return (
    <>
      {isProcessing && (
        <>
          <Header />
          <LinearProgress variant="indeterminate" />
          <Box sx={{ mt: 2 }}>
            <Typography variant="b2Medium" sx={{ mb: 1 }}>
              Your changes are being processed.
            </Typography>
            <Typography variant="b2Regular">Please don't refresh the page or press the back button.</Typography>
          </Box>
        </>
      )}
      <Box className={isProcessing ? 'Mui-hidden' : ''}>
        <Box className="home-container">
          {isMobileApp ? (
            <Box sx={{ mt: 3 }} />
          ) : breadcrumbItems.length > 1 ? (
            <Breadcrumb items={breadcrumbItems}></Breadcrumb>
          ) : null}
          <Box className="title-container">
            <Typography variant="h3" className="header" component="h1">
              Manage Auto Pay
            </Typography>
            <Typography variant="b1Regular">
              Enroll in Auto Pay and manage your {membershipDetails && ('membership and ')}insurance Auto Pay methods.
            </Typography>
          </Box>
          {disableAutoPayEnrollAndUpdateCTA && <Box className="warning-container">{renderAlert()}</Box>}
          <Box className={`body-container ${disableAutoPayEnrollAndUpdateCTA ? 'warning' : ''}`}>
            <Box className="products-container">
              {membershipElement}
              {productElements}
            </Box>
            <Typography variant="b1Bold" className="accepted-methods-title">
              Accepted payment methods
            </Typography>
            {acceptedMethodsText()}
            <span style={{ display: 'flex', flexDirection: 'row' }}>
              <Typography variant="b1Regular" id="paragraph-three">
                If you need assistance managing your Auto Pay methods for {membershipDetails && ('membership or ')}insurance, call{' '}
                <Link target="_self" href={`tel:${phoneNumber}`}>
                  {phoneNumber}
                </Link>
                .
              </Typography>
            </span>
            {hasECheckOnlyPolicy && (
              <span style={{ display: 'flex', flexDirection: 'row' }}>
                <Typography variant="b1Regular" className="watercraft">
                  &#185;{eCheckOnlyPolicyTypes} insurances are only accepting e-check for Auto Pay.
                </Typography>
              </span>
            )}
            <Box className="payment-methods"></Box>
          </Box>
          {renderTermsAndConditions()}
        </Box>
      </Box>
    </>
  );
};

function removeQueryParam(url: string, param: string) {
  const urlObject = new URL(url);
  const searchParams = urlObject.searchParams;
  searchParams.delete(param);
  urlObject.search = searchParams.toString();
  return urlObject.toString();
}
